import type { WorkspaceOut } from "@sagot/api-client";
import { canonicalIndustry, type IndustryKey } from "@sagot/constants";
import { useState } from "react";
import { answersFor, fieldsForTemplate, missingAnswers, rankTemplates, type ProfileAnswers } from "./ranking";
import { useSetupGoals, useTemplates } from "./use-catalog";
import { readOnboarding } from "./use-onboarding";

const FALLBACK_TEMPLATE = "blank";

/**
 * Step 1 choices: industry → goals → template → follow-up answers. The recommendation follows
 * industry + goals (template data); an explicit pick sticks until the industry changes. The
 * follow-up questions follow the SELECTED template (see `fieldsForTemplate`); answers are kept
 * for every key that still applies, so switching template back and forth loses nothing.
 */
export function useSetupChoices(workspace: WorkspaceOut | undefined) {
  const saved = readOnboarding(workspace).data;
  const templates = useTemplates();
  const goalsQuery = useSetupGoals();
  const [industry, setIndustryState] = useState<IndustryKey | null>(canonicalIndustry(workspace?.industry));
  const [industryLabel, setIndustryLabel] = useState(workspace?.industry_label ?? "");
  const [goals, setGoals] = useState<string[]>(workspace?.goals ?? []);
  const [storedAnswers, setStoredAnswers] = useState<ProfileAnswers>((workspace?.profile ?? {}) as ProfileAnswers);
  const [picked, setPicked] = useState<string | null>(saved.template_key ?? null);
  const [description, setDescription] = useState(saved.description ?? "");
  const all = templates.data ?? [];
  const ranked = industry ? rankTemplates(all, industry, goals) : [];
  const recommended = ranked[0]?.key;
  const chosen = picked && ranked.some((tpl) => tpl.key === picked) ? picked : null;
  const template = chosen ?? recommended ?? FALLBACK_TEMPLATE;
  const fields = industry ? fieldsForTemplate(all, industry, template) : [];
  const answers = answersFor(fields, storedAnswers);

  const setIndustry = (next: IndustryKey) => {
    setIndustryState(next);
    setPicked(null);
  };
  return {
    templates,
    goalsQuery,
    industry,
    setIndustry,
    industryLabel,
    setIndustryLabel,
    goals,
    setGoals,
    answers,
    /** Merges visible answers over the stored ones (answers of hidden questions are kept). */
    setAnswers: (next: ProfileAnswers) => { setStoredAnswers((current) => ({ ...current, ...next })); },
    fields,
    ranked,
    recommended,
    template,
    pickTemplate: setPicked,
    description,
    setDescription,
    missing: missingAnswers(fields, answers),
  };
}

export type SetupChoices = ReturnType<typeof useSetupChoices>;
